import type { LensCardData } from '~/types/leLens';

export type CardResult = { rows: Array<Record<string, unknown>>; truncated: boolean; card: LensCardData };
export type CardProblem = { problem: string };

export function readCard(cards: ReadonlyArray<LensCardData>, cardId: string): CardResult | CardProblem {
  const card = cards.find((item) => item.cardId === cardId);
  if (!card) {
    return { problem: 'This section is still loading.' };
  }
  if (card.status === 'ready' || card.status === 'empty') {
    return { rows: card.rows ?? [], truncated: Boolean(card.truncated), card };
  }
  return { problem: `This section could not load. ${card.message ?? ''}`.trim() };
}

export const isProblem = (result: CardResult | CardProblem): result is CardProblem => 'problem' in result;

export const num = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(typeof value === 'object' && value && 'value' in value ? value.value : value);
  return Number.isFinite(parsed) ? parsed : null;
};

/** A ratio of two sums; null rather than a misleading 0 when either side is unknown or the base is zero. */
export const ratio = (top: number | null, base: number | null): number | null =>
  top === null || base === null || base === 0 ? null : top / base;

/** Change against the previous value, divided by its absolute value: the workbook's LOOKUP(…, -1) growth. */
export const growth = (current: number | null, previous: number | null): number | null =>
  current === null || previous === null || previous === 0 ? null : (current - previous) / Math.abs(previous);

/** The API returns a bucketed date under the date column's own name. */
export const DATE_KEY = 'transaction_date_sydt';

export const text = (value: unknown): string => {
  if (value === null || value === undefined) {
    return 'Unknown';
  }
  // BigQuery DATE and BIGNUMERIC values can arrive wrapped as { value }.
  if (typeof value === 'object') {
    return 'value' in value ? text(value.value) : JSON.stringify(value);
  }
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }
  return typeof value === 'string' ? value : 'Unknown';
};

type Pivot = {
  rows: Array<string>;
  columns: Array<string>;
  cell: (row: string, column: string) => number | null;
  rowTotal: (row: string) => number | null;
  columnTotal: (column: string) => number | null;
};

/** Cells with no rows are null, not zero. */
export function pivot(
  rows: ReadonlyArray<Record<string, unknown>>,
  rowKey: string,
  columnKey: string,
  value: (row: Record<string, unknown>) => number | null,
): Pivot {
  const cells = new Map<string, number>();
  const rowTotals = new Map<string, number>();
  const columnTotals = new Map<string, number>();
  const add = (map: Map<string, number>, key: string, amount: number) => map.set(key, (map.get(key) ?? 0) + amount);
  for (const row of rows) {
    const amount = value(row);
    const r = text(row[rowKey]);
    const c = text(row[columnKey]);
    if (!rowTotals.has(r)) {
      rowTotals.set(r, 0);
    }
    if (!columnTotals.has(c)) {
      columnTotals.set(c, 0);
    }
    if (amount === null) {
      continue;
    }
    add(cells, `${r}\u0000${c}`, amount);
    add(rowTotals, r, amount);
    add(columnTotals, c, amount);
  }
  const columns = [...columnTotals.keys()].sort();
  const sortedRows = [...rowTotals.keys()].sort((a, b) => (rowTotals.get(b) ?? 0) - (rowTotals.get(a) ?? 0));
  return {
    rows: sortedRows,
    columns,
    cell: (row, column) => cells.get(`${row}\u0000${column}`) ?? null,
    rowTotal: (row) => rowTotals.get(row) ?? null,
    columnTotal: (column) => columnTotals.get(column) ?? null,
  };
}

const currencyFormat = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });
const countFormat = new Intl.NumberFormat('en-AU', { maximumFractionDigits: 0 });
const decimalFormat = new Intl.NumberFormat('en-AU', { maximumFractionDigits: 2 });

export type ValueFormat = 'currency' | 'count' | 'percent' | 'decimal';

export function formatValue(value: number | null, format: ValueFormat): string {
  if (value === null) {
    return '—';
  }
  if (format === 'currency') {
    return currencyFormat.format(value);
  }
  if (format === 'count') {
    return countFormat.format(value);
  }
  if (format === 'percent') {
    return `${(value * 100).toFixed(1)}%`;
  }
  return decimalFormat.format(value);
}

/** Currency is exported to the cent, as displayed precision; counts and ratios keep full precision. */
export function csvValue(value: number | null, format: ValueFormat): number | null {
  return value !== null && format === 'currency' ? Math.round(value * 100) / 100 : value;
}

export function csvOf(header: Array<string>, lines: Array<Array<string | number | null>>): string {
  const cell = (value: string | number | null) => {
    if (value === null) {
      return '';
    }
    const raw = String(value);
    const safe = /^[=+\-@]/.test(raw) && typeof value === 'string' ? `'${raw}` : raw;
    return /[",\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
  };
  return [header, ...lines].map((line) => line.map(cell).join(',')).join('\n');
}

export function download(name: string, content: string) {
  const url = URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
}
